import { CoinsIcon } from '@phosphor-icons/react';

import { balanceFraction, tierLabel, type TeamBalance } from '@/api-models/balance';
import { formatUsd } from '@/api-models/usage';
import { Card } from '@/ui/Card';

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * What this space has left to spend, and when it renews.
 *
 * Enforcement stops agent work the moment this reaches zero, so the figure has to
 * be visible to the people it stops — a refusal a user cannot explain is a support
 * ticket. It sits with the spend it bounds rather than in its own section, for the
 * same reason the budget cap does.
 *
 * Read-only by design: a space cannot grant itself credit. Changing the licence is
 * a Zooid staff action, so the card says who to ask rather than offering a control
 * that would only ever fail.
 */
export function CreditsCard({ balance }: { balance: TeamBalance }) {
  const remaining = balanceFraction(balance);
  const isLow = !balance.is_exhausted && remaining < 0.1;

  const barTone = balance.is_exhausted ? 'bg-danger-solid' : isLow ? 'bg-warning' : 'bg-brand';

  return (
    <Card radius="xl" padding="md" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-tile bg-surface-4">
            <CoinsIcon size={18} weight="duotone" className="text-fg-secondary" />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <h2 className="card-heading">Credits</h2>
            <p className="text-xs leading-4.25 text-fg-tertiary">
              {balance.is_exhausted
                ? 'This workspace is out of usage credit — agent sessions will not start. Ask your Zooid contact to add credit or upgrade the licence.'
                : `${tierLabel(balance.license_tier)} licence · renews ${formatDay(balance.period_end)}`}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-end">
          <span className={`text-lg font-medium tabular-nums ${balance.is_exhausted ? 'text-danger' : 'text-fg'}`}>
            {formatUsd(balance.available_micro_usd)}
          </span>
          <span className="text-2xs text-fg-muted">available</span>
        </div>
      </div>

      <div className="h-1.5 overflow-hidden rounded-full bg-surface-4">
        <div className={`h-full rounded-full ${barTone}`} style={{ width: `${remaining * 100}%` }} />
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-2xs text-fg-muted tabular-nums">
        <span>
          Allowance <span className="text-fg-secondary">{formatUsd(balance.allowance_micro_usd)}</span>
        </span>
        <span>
          Spent this period <span className="text-fg-secondary">{formatUsd(balance.period_spent_micro_usd)}</span>
        </span>
        <span>
          Credits <span className="text-fg-secondary">{formatUsd(balance.credit_balance_micro_usd)}</span>
        </span>
        <span>
          Period {formatDay(balance.period_start)} – {formatDay(balance.period_end)}
        </span>
      </div>

      {balance.credit_balance_micro_usd < 0 && (
        <p className="text-2xs text-fg-muted">
          A previous period went over its allowance. The difference is being recovered from this period&rsquo;s
          allowance.
        </p>
      )}
    </Card>
  );
}
